import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ParsedLine {
  lineNum: number;
  raw: string;
  /** True when this line ends with an un-closed `{` or `[`. */
  isFoldStart: boolean;
  /**
   * 1-based line number of the matching `}` / `]` close line.
   * `null` when no matching close was found (shouldn't occur in valid JSON).
   */
  foldEndLine: number | null;
}

// ─── Parsing ──────────────────────────────────────────────────────────────────

/** Empty container on its own line, e.g. `{}` or `[]` (with optional trailing comma). */
const RE_STANDALONE_EMPTY = /^[{[]\s*[}\]]\s*,?\s*$/;

/** Inline empty container after a key, e.g. `"key": {}` or `"key": []`. */
const RE_INLINE_EMPTY_VALUE = /:\s*[{[]\s*[}\]]\s*,?\s*$/;

/**
 * Split a JSON string into structured lines with fold-start / fold-end
 * relationships pre-computed via a stack.
 */
function parseLines(jsonStr: string): ParsedLine[] {
  const rawLines = jsonStr.split('\n');

  const parsed: ParsedLine[] = rawLines.map((raw, idx) => {
    const trimmed = raw.trim();
    // A line opens a foldable block when it ends with `{` or `[`.
    // Exclude inline empty containers like `{}` or `[]`.
    const isFoldStart =
      trimmed.length > 0 &&
      /[{[]\s*$/.test(trimmed) &&
      !RE_STANDALONE_EMPTY.test(trimmed) &&
      !RE_INLINE_EMPTY_VALUE.test(trimmed);

    return { lineNum: idx + 1, raw, isFoldStart, foldEndLine: null };
  });

  // Match each fold-start to its corresponding fold-end using a stack of indices.
  const stack: number[] = [];
  for (let i = 0; i < parsed.length; i++) {
    if (parsed[i].isFoldStart) {
      stack.push(i);
    } else if (/^[}\]]/.test(parsed[i].raw.trim()) && stack.length > 0) {
      const startIdx = stack.pop()!;
      parsed[startIdx].foldEndLine = parsed[i].lineNum;
    }
  }

  return parsed;
}

// ─── Component ────────────────────────────────────────────────────────────────

interface JsonViewerProps {
  jsonStr: string;
}

const JsonViewer: React.FC<JsonViewerProps> = ({ jsonStr }) => {
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());

  const lines = useMemo(() => parseLines(jsonStr), [jsonStr]);

  /** Width (in `ch` units) of the widest line number so the gutter stays aligned. */
  const gutterWidth = String(lines.length).length + 1;

  const toggleFold = (lineNum: number) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(lineNum)) {
        next.delete(lineNum);
      } else {
        next.add(lineNum);
      }
      return next;
    });
  };

  /**
   * Build the list of lines to render, skipping the interior of any collapsed
   * block.  A collapsed fold-start is rendered as a single summary row.
   */
  const visibleRows = useMemo(() => {
    const result: Array<ParsedLine & { isCollapsed: boolean }> = [];
    let skipUntilLine = -1;

    for (const line of lines) {
      if (skipUntilLine >= 0 && line.lineNum <= skipUntilLine) continue;
      skipUntilLine = -1;

      const isCollapsed =
        line.isFoldStart &&
        line.foldEndLine !== null &&
        collapsed.has(line.lineNum);

      result.push({ ...line, isCollapsed });

      if (isCollapsed && line.foldEndLine !== null) {
        skipUntilLine = line.foldEndLine;
      }
    }

    return result;
  }, [lines, collapsed]);

  return (
    <Box
      sx={{
        fontFamily: '"Consolas", "Monaco", "Courier New", monospace',
        fontSize: '0.8rem',
        overflow: 'auto',
        maxHeight: '60vh',
        userSelect: 'text',
        bgcolor: (theme) =>
          theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
        borderTop: '1px solid',
        borderColor: 'divider',
        py: 0.5,
      }}
    >
      {visibleRows.map(({ lineNum, raw, isFoldStart, foldEndLine, isCollapsed }) => {
        // When collapsed, grab the closing bracket from the matching close line
        // so we can show it inline (e.g. `"stories": [ …]`).
        // Derive the fallback from the opening line itself in case the close
        // line is somehow unreachable (shouldn't occur in valid JSON).
        const closingText = (() => {
          if (!isCollapsed || foldEndLine === null) return '';
          const closeLine = lines[foldEndLine - 1];
          if (closeLine) return closeLine.raw.trim();
          // Fallback: infer closing bracket from the opening line
          return /\[\s*$/.test(raw.trim()) ? ']' : '}';
        })();

        const hasFoldControl = isFoldStart && foldEndLine !== null;

        return (
          <Box
            key={lineNum}
            sx={{
              display: 'flex',
              alignItems: 'center',
              lineHeight: '1.6rem',
              minWidth: 'max-content',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            {/* ── Line-number gutter ── */}
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                minWidth: `${gutterWidth}ch`,
                textAlign: 'right',
                color: 'text.disabled',
                userSelect: 'none',
                px: 1.5,
                flexShrink: 0,
                borderRight: '1px solid',
                borderColor: 'divider',
                fontVariantNumeric: 'tabular-nums',
                lineHeight: '1.6rem',
              }}
            >
              {lineNum}
            </Box>

            {/* ── Fold-toggle column ── */}
            <Box
              sx={{
                width: '1.4rem',
                height: '1.6rem',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {hasFoldControl ? (
                <Box
                  component="button"
                  onClick={() => toggleFold(lineNum)}
                  aria-label={isCollapsed ? 'Expand block' : 'Collapse block'}
                  sx={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    p: 0,
                    lineHeight: 0,
                    display: 'flex',
                    alignItems: 'center',
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  {isCollapsed ? (
                    <ChevronRightIcon sx={{ fontSize: '0.85rem' }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: '0.85rem' }} />
                  )}
                </Box>
              ) : null}
            </Box>

            {/* ── Code content ── */}
            <Box
              component="span"
              sx={{ whiteSpace: 'pre', lineHeight: '1.6rem', pl: 0.5 }}
            >
              {isCollapsed ? (
                <>
                  {raw.trimEnd()}
                  <Box component="span" sx={{ color: 'text.disabled' }}>
                    {' \u2026'}
                    {closingText}
                  </Box>
                </>
              ) : (
                raw
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default JsonViewer;
